import {
  TRANSFORMATION_STATUS_LABELS,
  type ProductionActualOutput,
  type StockTransformation,
} from "@artisancode/api-types";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Coffee,
  Factory,
  User,
  XCircle,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useHasPermission } from "@/hooks/use-auth";
import { formatDateWIB } from "@/lib/date-utils";
import { formatCurrency, formatCurrencyRounded } from "@/lib/utils";

import { ProductionCompleteConfirm } from "./production-complete-confirm";
import { ProductionInputLines } from "./production-input-lines";
import { summarizeProduction } from "./production-utils";

interface Props {
  transformation: StockTransformation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (
    id: string,
    actualOutputs?: ProductionActualOutput[],
  ) => Promise<void>;
  onCancelRequest: (trans: StockTransformation) => void;
  isCompleting?: boolean;
}

export function ProductionDetailDialog({
  transformation,
  open,
  onOpenChange,
  onComplete,
  onCancelRequest,
  isCompleting,
}: Props) {
  const canComplete = useHasPermission("stock_transformations.complete");
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  if (!transformation) return null;

  const statusMeta = TRANSFORMATION_STATUS_LABELS[transformation.status];
  const isDraft = transformation.status === "draft";
  const isCompleted = transformation.status === "completed";
  const isCancelled = transformation.status === "cancelled";
  const { menus } = summarizeProduction(transformation);

  const handleConfirmComplete = async (
    actualOutputs: ProductionActualOutput[],
  ) => {
    await onComplete(transformation.id, actualOutputs);
    setShowCompleteConfirm(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Factory className="h-5 w-5 text-primary" />
              <DialogTitle className="font-mono text-lg">
                {transformation.code}
              </DialogTitle>
            </div>
            <Badge variant={statusMeta.variant} className="text-xs">
              {statusMeta.label}
            </Badge>
          </div>
          <DialogDescription>
            Rincian batch produksi barang jadi dan konsumsi bahan baku.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Metadata Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Tanggal</span>
              </div>
              <div className="mt-1 text-sm font-semibold">
                {formatDateWIB(transformation.date, { dateStyle: "full" })}
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Coffee className="h-3.5 w-3.5" />
                <span>Menu Diproduksi</span>
              </div>
              <div className="mt-1 text-sm font-semibold truncate">
                {menus.length} menu
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <User className="h-3.5 w-3.5" />
                <span>Operator</span>
              </div>
              <div className="mt-1 text-sm font-semibold truncate">
                {transformation.createdByName || "Barista Toko"}
              </div>
            </div>

            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                <span>Status Stok</span>
              </div>
              <div className="mt-1 text-sm font-semibold">
                {isCompleted
                  ? "Sudah Masuk Toko"
                  : isCancelled
                    ? "Dibatalkan"
                    : "Belum Masuk Toko"}
              </div>
            </div>
          </div>

          {/* Cancellation Notice if Cancelled */}
          {isCancelled && transformation.cancelReason && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Dokumen Ini Telah Dibatalkan</p>
                <p className="mt-0.5">{transformation.cancelReason}</p>
              </div>
            </div>
          )}

          {/* One output card + its ingredients per menu */}
          {menus.map(({ outputLine, inputLines, inputCost, unitCost }) => (
            <div key={outputLine.id} className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Barang Jadi Dihasilkan (Output)</span>
                </div>
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="text-sm sm:text-base font-bold text-foreground">
                        {outputLine.itemName}
                      </div>
                      {outputLine.itemSku && (
                        <div className="text-xs text-muted-foreground font-mono">
                          {outputLine.itemSku}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-6">
                      <div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground">
                          Jumlah Output
                        </div>
                        <div className="text-sm sm:text-lg font-bold font-mono text-primary">
                          {outputLine.qty.toLocaleString("id-ID")}{" "}
                          <span className="text-[10px] sm:text-xs font-normal">
                            {outputLine.uomName}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground">
                          HPP Satuan
                        </div>
                        <div className="text-sm sm:text-lg font-bold font-mono">
                          {formatCurrency(unitCost)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground">
                          Total Nilai
                        </div>
                        <div className="text-sm sm:text-lg font-bold font-mono">
                          {formatCurrencyRounded(outputLine.qty * unitCost)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <ProductionInputLines lines={inputLines} totalCost={inputCost} />
            </div>
          ))}

          {/* Notes */}
          {transformation.note && (
            <div className="rounded-lg border bg-muted/30 p-3 text-xs">
              <span className="font-semibold text-muted-foreground">
                Catatan:
              </span>{" "}
              {transformation.note}
            </div>
          )}

          {showCompleteConfirm && (
            <ProductionCompleteConfirm
              menus={menus}
              isCompleting={isCompleting}
              onConfirm={handleConfirmComplete}
              onCancel={() => setShowCompleteConfirm(false)}
            />
          )}
        </div>

        <DialogFooter className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:gap-2">
          <div>
            {!isCancelled && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onCancelRequest(transformation)}
                className="w-full sm:w-auto min-h-[40px] sm:min-h-9"
              >
                <XCircle className="mr-1.5 h-4 w-4" />
                Batalkan Batch
              </Button>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="min-h-[40px] sm:min-h-9"
            >
              Tutup
            </Button>
            {isDraft && canComplete && !showCompleteConfirm && (
              <Button
                type="button"
                onClick={() => setShowCompleteConfirm(true)}
                className="min-h-[40px] sm:min-h-9"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                Selesaikan Produksi
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
